/**
 * 多语言。**纯逻辑，不含 React** —— 能用 node:test 直接测。
 *
 * 所以这个文件（以及其它被 node:test 直接跑的 `.ts`）的相对导入**必须带
 * `.ts` 扩展名**：Vite 能补，node 的 ESM 不能。`.tsx` 不用管，node 从不加载它们。
 *
 * 三件事，缺一件这个功能就是半成品：
 *   翻译    字符串照着语言换
 *   复数    英语葡语要区分单复数，中文不用（Intl.PluralRules）
 *   格式    金额、数字、时间 —— **这才是最容易漏的那一半**。
 *           只翻字符串、金额还是手拼 `R$ 1.234,56` 的话，换成英文就错了
 *           （英文该是 R$ 1,234.56，小数点和千分位正好相反）。
 */
import { en } from './messages/en.ts';
import { fil } from './messages/fil.ts';
import { ptBR } from './messages/pt-BR.ts';
import { zhCN, type MessageKey, type RawKey } from './messages/zh-CN.ts';

export type { MessageKey, RawKey };

export const LOCALES = ['pt-BR', 'en', 'zh-CN', 'fil'] as const;
export type Locale = (typeof LOCALES)[number];

/** 语言自己的名字。**永远用母语写** —— 一个只看得懂葡语的人
 *  在中文界面里要能认出 "Português"，写成"葡萄牙语"他就找不到。 */
export const LOCALE_NAME: Record<Locale, string> = {
  'pt-BR': 'Português',
  en: 'English',
  'zh-CN': '中文',
  fil: 'Filipino',
};

/** 主要市场是巴西 —— 认不出来的浏览器语言落到这儿。 */
export const DEFAULT_LOCALE: Locale = 'pt-BR';

const CATALOG: Record<Locale, Record<RawKey, string>> = {
  'zh-CN': zhCN,
  en,
  'pt-BR': ptBR,
  fil,
};

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/**
 * 把浏览器给的语言列表配到我们支持的语言上。
 *
 * 按**语言子标签**配，不要求整串相等：`pt`、`pt-PT`、`pt-br` 都该落到 `pt-BR`，
 * 否则一个巴西用户把系统语言写成 `pt` 就会看到葡语之外的东西。
 */
export function negotiate(preferred: readonly string[]): Locale {
  for (const raw of preferred) {
    const tag = String(raw || '').toLowerCase();
    const exact = LOCALES.find((l) => l.toLowerCase() === tag);
    if (exact) return exact;
    const base = tag.split('-')[0];
    // 浏览器也可能用 Tagalog 的语言代码表示菲律宾语。
    if (base === 'tl') return 'fil';
    const byBase = LOCALES.find((l) => l.toLowerCase().split('-')[0] === base);
    if (byBase) return byBase;
  }
  return DEFAULT_LOCALE;
}

const KEY = 'lobby.locale';

export function loadLocale(
  store: Pick<Storage, 'getItem'> | null,
  preferred: readonly string[] = [],
): Locale {
  try {
    const saved = store?.getItem(KEY);
    if (isLocale(saved)) return saved;
  } catch { /* 隐私模式：往下走浏览器语言 */ }
  return negotiate(preferred);
}

export function saveLocale(store: Pick<Storage, 'setItem'> | null, l: Locale): void {
  try { store?.setItem(KEY, l); } catch { /* 存不下也不影响本次会话 */ }
}

/** `{n}` 这种占位。**只做替换，不做表达式** —— 文案是给翻译的人写的，
 *  里面不该出现逻辑。 */
function interpolate(tpl: string, params?: Record<string, string | number>): string {
  if (!params) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (m, k) =>
    (k in params ? String(params[k]) : m));
}

export class I18n {
  readonly locale: Locale;
  #msg: Record<RawKey, string>;
  #plural: Intl.PluralRules;
  #num: Intl.NumberFormat;

  constructor(locale: Locale) {
    this.locale = locale;
    this.#msg = CATALOG[locale];
    this.#plural = new Intl.PluralRules(locale);
    this.#num = new Intl.NumberFormat(locale);
  }

  /**
   * 取一条文案。
   *
   * 带 `n` 时先按复数规则挑 `key_one` / `key_other`（中文两个一样，
   * 英语葡语不一样）。挑不到就退回不带后缀的那个键。
   *
   * **取不到时返回键名本身，不抛也不留空** —— 界面上出现 `auth.phone`
   * 很难看，但比一片空白好查得多，而且不会把整个页面带崩。
   */
  t(key: MessageKey, params?: Record<string, string | number>): string {
    let k: string = key;
    if (params && typeof params.n === 'number') {
      const cat = this.#plural.select(params.n);
      const withCat = `${key}_${cat}`;
      if (withCat in this.#msg) k = withCat;
      else if (`${key}_other` in this.#msg) k = `${key}_other`;
    }
    const tpl = this.#msg[k as RawKey];
    if (tpl === undefined) return key;
    return interpolate(tpl, params);
  }

  /** 数字。千分位和小数点按语言走 —— 手拼的话换语言就错。 */
  num(n: number): string {
    return this.#num.format(n);
  }

  /**
   * 金额。**币种符号的位置也是本地化的一部分**：
   * pt-BR 是 `R$ 1.234,56`，en 是 `R$1,234.56`，中文是 `BRL 1,234.56`。
   *
   * 币种由服务端下发（一个部署一个币种），连同符号和小数位一起 ——
   * 前端**不维护币种对照表**，那张表迟早会和服务端的配置对不上。
   *
   * `opts.symbol` 覆盖的只是符号本身，**位置和间距仍然交给 Intl**：
   * 符号在前还是在后、中间有没有空格，各语言不一样，手拼一定会在某个语言上错。
   */
  money(amount: number, currency: string,
        opts?: { symbol?: string; decimals?: number }): string {
    const dec = opts?.decimals;
    const digits = dec === undefined
      ? {}
      : { minimumFractionDigits: dec, maximumFractionDigits: dec };
    try {
      const f = new Intl.NumberFormat(this.locale,
        { style: 'currency', currency, ...digits });
      if (!opts?.symbol) return f.format(amount);
      return f.formatToParts(amount)
        .map((part) => (part.type === 'currency' ? opts.symbol! : part.value))
        .join('');
    } catch {
      // 币种代码 Intl 不认识（自己开一个新市场时就会这样）—— 别让它把页面崩掉。
      // 退回纯数字格式化，符号手动拼上：位置会不地道，但金额是对的。
      const n = new Intl.NumberFormat(this.locale, digits).format(amount);
      return `${opts?.symbol ?? currency} ${n}`;
    }
  }

  /**
   * 日期时间。**跟应用选的语言走，不跟浏览器走。**
   *
   * `Date.toLocaleString()` 不传 locale 的话用的是浏览器语言 ——
   * 一个把界面切成葡语的人，日期还会是 `9/10/2026, 7:33 PM`（美式）。
   * 整个 i18n 层的意义就是这件事不该发生。
   *
   * 用 `medium` 而不是 `short`：`short` 下英语是 `9/11/26`、葡语是 `11/09/2026`
   * —— **同一串数字在两种语言下含义正好相反**。日期出现在提现记录这种地方，
   * 那种歧义会变成客服工单。`medium` 的月份不是数字（`Sep` / `set.`），
   * 看错的可能性没有了。
   */
  dateTime(ms: number): string {
    return new Intl.DateTimeFormat(this.locale,
      { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms));
  }

  /** "多久以前"。用 Intl.RelativeTimeFormat 的话各语言的句式都不用自己拼，
   *  但我们的文案里有"刚刚活跃"这种非相对时间的说法，所以还是走文案表。 */
  idle(seconds: number): string {
    if (seconds < 60) return this.t('time.justNow');
    if (seconds < 3600) return this.t('time.minutesAgo', { n: Math.floor(seconds / 60) });
    if (seconds < 86400) return this.t('time.hoursAgo', { n: Math.floor(seconds / 3600) });
    return this.t('time.daysAgo', { n: Math.floor(seconds / 86400) });
  }
}

/** `<html lang>` 要跟着变：影响朗读、断词、以及浏览器给的默认字体。 */
export function applyLocale(root: HTMLElement, l: Locale): void {
  root.lang = l;
}
